/**
 * TransferRequestSheet — small sheet a manager opens from the Crew
 * Management row to request a transfer. Picks a target store from the
 * district, optional reason, submits, closes. The pending request then
 * shows up in the Admin panel's Transfers section for DM/admin approval.
 * (2026-02-27 user ask "make transfers easier than deleting them off
 * the roster and moving them".)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ArrowsLeftRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransferRequestSheet({ open, crew, actorId, onClose, onRequested }) {
  const [stores, setStores] = useState([]);
  const [toStoreId, setToStoreId] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/transfers/eligible-stores`, {
          params: { actor_id: actorId },
        });
        setStores(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't load stores.");
      }
    })();
    setToStoreId("");
    setReason("");
  }, [open, actorId]);

  if (!open || !crew) return null;

  const eligibleStores = stores.filter((s) => s.id !== (crew.home_store_id || "store_3094"));

  const submit = async () => {
    if (submitting) return;
    if (!toStoreId) { toast.error("Pick a destination store."); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/transfers/crew/${crew.id}/request`, {
        actor_id: actorId,
        to_store_id: toStoreId,
        reason: reason.trim(),
      });
      // Admin/DM requesters auto-approve — server returns the approved
      // transfer row directly instead of a "pending" one. Tell the user
      // which happened so they know whether Kim needs to wait or not.
      const status = res.data?.status || "pending";
      if (status === "approved") {
        toast.success(`Transfer complete — ${crew.first_name || "crew"} moved.`);
      } else {
        toast.success("Transfer request sent to your DM/admin for approval.");
      }
      onRequested?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit request.");
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[9500] bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="transfer-request-sheet"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', Inter, sans-serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-shrink-0">
          <h3
            className="text-[18px] font-black text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "'Five Below Gothic', Chivo, -apple-system, sans-serif", letterSpacing: "-0.01em" }}
          >
            <ArrowsLeftRight weight="bold" className="w-5 h-5 text-[#1D428A]" />
            Request transfer
          </h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" aria-label="Close" data-testid="transfer-request-close">
            <X weight="regular" className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-3 py-2.5">
            {crew.photo_url ? (
              <img src={crew.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#1D428A] text-white flex items-center justify-center font-black">
                {(crew.first_name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[15px] font-bold text-slate-900 leading-tight truncate">{crew.first_name}</p>
              <p className="text-[12px] text-slate-500 leading-tight">{crew.position || "Crew"}{crew.is_manager ? " · Manager" : ""}</p>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">Move to</label>
            {eligibleStores.length === 0 ? (
              <p className="text-[13px] text-slate-500 italic">No other stores are available.</p>
            ) : (
              <div className="max-h-56 overflow-y-auto rounded-2xl border border-slate-200 divide-y divide-slate-100">
                {eligibleStores.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => setToStoreId(s.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${toStoreId === s.id ? "bg-blue-50" : "hover:bg-slate-50"}`}
                    data-testid={`transfer-store-${s.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-[14px] font-semibold text-slate-900 truncate">
                        {s.name || s.id}
                        {s.store_number ? <span className="text-slate-400 font-medium ml-1">#{s.store_number}</span> : null}
                      </p>
                      {s.city && <p className="text-[11px] text-slate-500">{s.city}{s.state ? `, ${s.state}` : ""}</p>}
                    </div>
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${toStoreId === s.id ? "bg-[#1D428A] border-[#1D428A]" : "border-slate-300"}`} aria-hidden="true" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Promotion, closer to home, cross-training…"
              rows={2}
              maxLength={400}
              className="w-full p-2.5 rounded-xl bg-slate-100 text-[14px] border-0 focus:outline-none focus:ring-2 focus:ring-blue-200"
              data-testid="transfer-reason"
            />
          </div>

          <p className="text-[11px] text-slate-500 leading-snug">
            Your DM/Admin will approve or reject this. The crew member keeps everything — attendance, celebrations, chat history, hire date, avatar — nothing gets wiped.
          </p>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-2 justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 h-10 rounded-full text-[14px] font-semibold text-slate-600 hover:bg-slate-100"
            data-testid="transfer-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!toStoreId || submitting}
            className={`px-5 h-10 rounded-full text-[14px] font-semibold ${(!toStoreId || submitting) ? "bg-slate-300 text-slate-500" : "bg-[#1D428A] text-white hover:bg-[#173772]"}`}
            data-testid="transfer-submit"
          >
            {submitting ? "Sending…" : "Send request"}
          </button>
        </div>
      </div>
    </div>
  );
}
